import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Plus, X, Loader2, Pencil, Info } from 'lucide-react';
import { DISCOVERY_SOURCE_LABEL, type DiscoverySource } from '@iomi/shared';
import { useUserProfile } from '../../context/UserProfileContext';
import { useOrgSettings, type OrgSettingsUpdate } from '../../hooks/useOrgSettings';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import type { Organization, BookingConfig } from '../../types/admin';

const isDevMode = !IS_FIREBASE_CONFIGURED;

const BOOKING_DOMAIN = 'booking.iomipro.com';

const DEFAULT_CONFIG: BookingConfig = {
  enabled: true,
  welcomeMessage: '',
  enableLockInDate: true,
  enableRequestEstimate: true,
  enableShowPriceEstimate: true,
  depositType: 'fixed',
  depositAmount: undefined,
  customDiscoverySources: [],
  enableOnlineQuote: false,
  enableAcceptPayments: true,
  brandPrimaryColor: '#6B2D8B',
  brandTitleTextColor: '#FFFFFF',
};

// Platform-provided discovery sources (read-only)
const PLATFORM_SOURCES = Object.entries(DISCOVERY_SOURCE_LABEL) as [DiscoverySource, string][];

export function OnlineLeadsSettingsPage({ view }: { view?: 'booking' | 'quotes' } = {}) {
  const { adminOrg, loading: profileLoading } = useUserProfile();
  const orgId = adminOrg?.orgId ?? '';

  if (profileLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="flex flex-col items-center py-20 gap-3 text-center px-4">
        <p className="text-small text-secondary-theme">You don&apos;t have access to org admin settings.</p>
      </div>
    );
  }

  return <OnlineLeadsSettingsContent orgId={orgId} view={view} />;
}

// ── Toggle component ─────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative w-12 h-7 rounded-full transition-colors ${
        checked ? 'bg-plum' : 'bg-subtle'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      aria-label={label}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ── Card Edit Button ────────────────────────────────────────────────────────

function CardEditButton({ editing, onEdit, onSave, onCancel, saving }: {
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  if (!editing) {
    return (
      <button
        type="button"
        onClick={onEdit}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-plum border border-plum rounded-lg hover:bg-plum/5 transition-colors"
      >
        <Pencil size={12} />
        Edit
      </button>
    );
  }
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-plum text-white rounded-lg hover:bg-plum/90 disabled:opacity-50 transition-colors"
      >
        {saving && <Loader2 size={12} className="animate-spin" />}
        Save
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="px-3 py-1.5 text-xs font-medium text-secondary-theme border border-theme rounded-lg hover:bg-subtle transition-colors"
      >
        Cancel
      </button>
    </div>
  );
}

// ── Tab Component ───────────────────────────────────────────────────────────

function TabBar({ activeTab, onTabChange }: {
  activeTab: 'lead_capture' | 'online_pdf_quotes';
  onTabChange: (tab: 'lead_capture' | 'online_pdf_quotes') => void;
}) {
  return (
    <div className="flex bg-mist rounded-lg p-1 gap-1">
      <button
        type="button"
        onClick={() => onTabChange('lead_capture')}
        className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150 ${
          activeTab === 'lead_capture'
            ? 'bg-white text-midnight shadow-brand-sm'
            : 'text-slate hover:text-midnight'
        }`}
      >
        Lead Capture
      </button>
      <button
        type="button"
        onClick={() => onTabChange('online_pdf_quotes')}
        className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150 ${
          activeTab === 'online_pdf_quotes'
            ? 'bg-white text-midnight shadow-brand-sm'
            : 'text-slate hover:text-midnight'
        }`}
      >
        Online &amp; PDF Quotes
      </button>
    </div>
  );
}

// ── Content ──────────────────────────────────────────────────────────────────

function OnlineLeadsSettingsContent({ orgId, view }: { orgId: string; view?: 'booking' | 'quotes' }) {
  const navigate = useNavigate();
  const { org, loading, error, refetch } = useOrgSettings(orgId);

  const savedConfig = useMemo<BookingConfig>(
    () => (org?.bookingConfig ? { ...DEFAULT_CONFIG, ...org.bookingConfig } : DEFAULT_CONFIG),
    [org],
  );
  const savedSlug = org?.slug ?? '';

  // Tab state — when view is set, lock to the appropriate tab
  const defaultTab = view === 'quotes' ? 'online_pdf_quotes' : 'lead_capture';
  const [activeTab, setActiveTab] = useState<'lead_capture' | 'online_pdf_quotes'>(defaultTab as 'lead_capture' | 'online_pdf_quotes');

  // ── Card-level edit states ──────────────────────────────────────────────

  // Enable/Disable card
  const [enableEditing, setEnableEditing] = useState(false);
  const [enableConfig, setEnableConfig] = useState<Partial<BookingConfig> | null>(null);
  const [enableSaving, setEnableSaving] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  // Booking URL card
  const [slugEditing, setSlugEditing] = useState(false);
  const [editSlug, setEditSlug] = useState<string | null>(null);
  const [slugSaving, setSlugSaving] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Customer Actions card
  const [actionsEditing, setActionsEditing] = useState(false);
  const [actionsConfig, setActionsConfig] = useState<Partial<BookingConfig> | null>(null);
  const [actionsSaving, setActionsSaving] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Welcome Message card
  const [welcomeEditing, setWelcomeEditing] = useState(false);
  const [welcomeConfig, setWelcomeConfig] = useState<Partial<BookingConfig> | null>(null);
  const [welcomeSaving, setWelcomeSaving] = useState(false);

  // Discovery Sources card
  const [sourcesEditing, setSourcesEditing] = useState(false);
  const [sourcesConfig, setSourcesConfig] = useState<Partial<BookingConfig> | null>(null);
  const [sourcesSaving, setSourcesSaving] = useState(false);
  const [newSource, setNewSource] = useState('');

  // PDF Quotes card
  const [pdfEditing, setPdfEditing] = useState(false);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfFooter, setPdfFooter] = useState(org?.quoteFooter ?? '');
  const [pdfEnabled, setPdfEnabled] = useState<boolean | null>(null);

  const slug = editSlug ?? savedSlug;

  const bookingUrl = slug ? `${BOOKING_DOMAIN}/${slug}` : null;

  // ── Save helpers ────────────────────────────────────────────────────────

  async function saveCardConfig(partial: Partial<BookingConfig>): Promise<boolean> {
    const merged = { ...savedConfig, ...partial };
    try {
      await api.patch<{ success: boolean; data: Organization }>(
        `/org-admin/${orgId}/booking-config`,
        merged,
      );
      await refetch();
      return true;
    } catch (err) {
      throw err;
    }
  }

  // ── Enable/Disable handlers ──────────────────────────────────────────

  const handleDisableToggle = () => {
    const currentEnabled = enableConfig?.enabled ?? savedConfig.enabled;
    if (currentEnabled) {
      setShowDisableConfirm(true);
    } else {
      setEnableConfig(prev => ({ ...prev, enabled: true }));
    }
  };

  const handleOnlineQuoteToggle = (v: boolean) => {
    setEnableConfig(prev => ({
      ...prev,
      enableOnlineQuote: v,
      ...(v ? { enableAcceptPayments: true } : {}),
    }));
  };

  async function confirmDisable() {
    setShowDisableConfirm(false);
    const disabledConfig = { ...savedConfig, ...enableConfig, enabled: false };
    setEnableSaving(true);
    try {
      await saveCardConfig(disabledConfig);
      setEnableConfig(null);
      setEnableEditing(false);
    } catch {
      // keep editing
    } finally {
      setEnableSaving(false);
    }
  }

  const handleEnableSave = async () => {
    if (!enableConfig) { setEnableEditing(false); return; }
    setEnableSaving(true);
    try {
      await saveCardConfig(enableConfig);
      setEnableConfig(null);
      setEnableEditing(false);
    } catch {
      // keep editing
    } finally {
      setEnableSaving(false);
    }
  };

  const handleEnableCancel = () => {
    setEnableConfig(null);
    setEnableEditing(false);
    setShowDisableConfirm(false);
  };

  // ── Slug handlers ────────────────────────────────────────────────────

  const handleCopy = useCallback(async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(`https://${bookingUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: no-op
    }
  }, [bookingUrl]);

  const handleSlugSave = async () => {
    if (!slug.trim()) return;
    setSlugSaving(true);
    setSlugError(null);
    try {
      await api.patch<{ success: boolean; data: Organization }>(
        `/org-admin/${orgId}/slug`,
        { slug: slug.trim().toLowerCase() },
      );
      await refetch();
      setEditSlug(null);
      setSlugEditing(false);
    } catch (err) {
      setSlugError(err instanceof Error ? err.message : 'Failed to update slug');
    } finally {
      setSlugSaving(false);
    }
  };

  const handleSlugCancel = () => {
    setEditSlug(null);
    setSlugEditing(false);
    setSlugError(null);
  };

  // ── Customer Actions handlers ──────────────────────────────────────────

  const handleActionsToggle = (key: keyof BookingConfig, value: boolean) => {
    setActionsConfig(prev => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const handleActionsSave = async () => {
    if (!actionsConfig) { setActionsEditing(false); return; }
    const merged = { ...savedConfig, ...actionsConfig };
    if (!merged.enableLockInDate && !merged.enableRequestEstimate) {
      setValidationError('At least one customer action must be enabled.');
      return;
    }
    setActionsSaving(true);
    setActionsError(null);
    try {
      await saveCardConfig(actionsConfig);
      setActionsConfig(null);
      setActionsEditing(false);
    } catch (err) {
      setActionsError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setActionsSaving(false);
    }
  };

  const handleActionsCancel = () => {
    setActionsConfig(null);
    setActionsEditing(false);
    setActionsError(null);
    setValidationError(null);
  };

  // ── Welcome Message handlers ────────────────────────────────────────────

  const handleWelcomeSave = async () => {
    if (!welcomeConfig) { setWelcomeEditing(false); return; }
    setWelcomeSaving(true);
    try {
      await saveCardConfig(welcomeConfig);
      setWelcomeConfig(null);
      setWelcomeEditing(false);
    } catch {
      // keep editing
    } finally {
      setWelcomeSaving(false);
    }
  };

  const handleWelcomeCancel = () => {
    setWelcomeConfig(null);
    setWelcomeEditing(false);
  };

  // ── Discovery Sources handlers ────────────────────────────────────────

  const addCustomSource = () => {
    const trimmed = newSource.trim();
    if (!trimmed) return;
    const current = sourcesConfig?.customDiscoverySources ?? savedConfig.customDiscoverySources ?? [];
    if (current.includes(trimmed)) return;
    setSourcesConfig(prev => ({
      ...prev,
      customDiscoverySources: [...current, trimmed],
    }));
    setNewSource('');
  };

  const removeCustomSource = (source: string) => {
    const current = sourcesConfig?.customDiscoverySources ?? savedConfig.customDiscoverySources ?? [];
    setSourcesConfig(prev => ({
      ...prev,
      customDiscoverySources: current.filter(s => s !== source),
    }));
  };

  const handleSourcesSave = async () => {
    if (!sourcesConfig) { setSourcesEditing(false); return; }
    setSourcesSaving(true);
    try {
      await saveCardConfig(sourcesConfig);
      setSourcesConfig(null);
      setSourcesEditing(false);
    } catch {
      // keep editing
    } finally {
      setSourcesSaving(false);
    }
  };

  const handleSourcesCancel = () => {
    setSourcesConfig(null);
    setSourcesEditing(false);
    setNewSource('');
  };

  // ── PDF Quotes handlers ────────────────────────────────────────────

  const isPdfEnabled = pdfEnabled ?? (isDevMode || (org?.featureQuotePdfs !== false));

  const handlePdfEdit = () => {
    setPdfFooter(org?.quoteFooter ?? '');
    setPdfEnabled(isDevMode || (org?.featureQuotePdfs !== false));
    setPdfEditing(true);
  };

  const handlePdfSave = async () => {
    setPdfSaving(true);
    try {
      const data: OrgSettingsUpdate = {
        featureQuotePdfs: pdfEnabled ?? undefined,
        quoteFooter: pdfFooter.trim(),
      };
      await api.patch<{ success: boolean; data: Organization }>(
        `/org-admin/${orgId}/settings`,
        data,
      );
      await refetch();
      setPdfEditing(false);
      setPdfEnabled(null);
    } catch {
      // keep editing
    } finally {
      setPdfSaving(false);
    }
  };

  const handlePdfCancel = () => {
    setPdfEditing(false);
    setPdfEnabled(null);
    setPdfFooter(org?.quoteFooter ?? '');
  };

  // ── Loading / Error states ────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-20 gap-3 text-center px-4">
        <p className="text-small text-red-500">{error}</p>
      </div>
    );
  }

  const hasOnlineLeadCapture = org?.featureOnlineLeadCapture !== false && org?.allowedFeatureOnlineLeadCapture !== false;
  const isFormEnabled = enableConfig?.enabled ?? savedConfig.enabled;
  const isOnlineQuoteEnabled = enableConfig?.enableOnlineQuote ?? savedConfig.enableOnlineQuote ?? false;

  // Effective values for Customer Actions
  const effectiveLockInDate = actionsConfig?.enableLockInDate ?? savedConfig.enableLockInDate;
  const effectiveRequestEstimate = actionsConfig?.enableRequestEstimate ?? savedConfig.enableRequestEstimate;
  const effectiveShowPrice = actionsConfig?.enableShowPriceEstimate ?? savedConfig.enableShowPriceEstimate ?? true;

  // Effective values for Welcome
  const effectiveWelcomeMessage = welcomeConfig?.welcomeMessage ?? savedConfig.welcomeMessage ?? '';

  // Effective values for Sources
  const effectiveCustomSources = sourcesConfig?.customDiscoverySources ?? savedConfig.customDiscoverySources ?? [];

  // PDF Quotes feature flag
  const allowedQuotePdfs = isDevMode || (org as unknown as Record<string, unknown>)?.allowedFeatureQuotePdfs !== false;

  return (
    <div className="px-4 pt-4 pb-8 md:px-8 md:max-w-2xl lg:max-w-4xl md:mx-auto">
      {/* Breadcrumbs */}
      {view && (
        <nav className="flex items-center gap-1.5 text-xs text-secondary-theme mb-2">
          <button type="button" onClick={() => navigate('/admin')} className="hover:text-plum transition-colors">
            Admin Panel
          </button>
          <span>&gt;</span>
          <button type="button" onClick={() => navigate('/admin/customer-facing')} className="hover:text-plum transition-colors">
            Customer-Facing
          </button>
        </nav>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(view ? '/admin/customer-facing' : '/admin')}
          className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-subtle transition-colors shrink-0"
          aria-label={view ? 'Back to Customer-Facing' : 'Back to Admin Panel'}
        >
          <ArrowLeft size={20} className="text-primary-theme" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-h1 text-primary-theme">
            {view === 'booking' ? 'Booking Form' : view === 'quotes' ? 'Online & PDF Quotes' : 'Online Leads & Quotes'}
          </h1>
          <p className="text-small text-secondary-theme">
            {view === 'booking'
              ? 'Lead intake form, URL slug, welcome message, and discovery sources.'
              : view === 'quotes'
                ? 'Customer quote portal and PDF quote generation.'
                : 'Configure your customer-facing booking form and online quotes.'}
          </p>
        </div>
      </div>

      {!hasOnlineLeadCapture && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-amber-800">
            Online Lead Capture is not enabled for your organization. Contact IOMI to enable this feature.
          </p>
        </div>
      )}

      {!org?.slug && hasOnlineLeadCapture && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-amber-800">
            Your organization needs a URL slug before the booking form can go live. Set one up in the Booking URL section below.
          </p>
        </div>
      )}

      <div className="space-y-5">

        {/* ═══════════════════════════════════════════════════════════════════
            TABS
            ═══════════════════════════════════════════════════════════════════ */}
        {!view && <TabBar activeTab={activeTab} onTabChange={setActiveTab} />}

        {/* ═══════════════════════════════════════════════════════════════════
            LEAD CAPTURE TAB
            ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'lead_capture' && (
          <div className="space-y-5">
            {/* Lead Intake Form Enabled — full width above grid */}
            <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-primary-theme">Lead Capture Settings</h2>
                <CardEditButton
                  editing={enableEditing}
                  onEdit={() => { setEnableConfig({}); setEnableEditing(true); }}
                  onSave={handleEnableSave}
                  onCancel={handleEnableCancel}
                  saving={enableSaving}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-theme font-medium">Lead Intake Form Enabled</p>
                  <p className="text-xs text-secondary-theme mt-0.5">
                    {isFormEnabled
                      ? 'Customers can access the booking form.'
                      : 'The booking form is currently disabled. Your configuration is preserved.'}
                  </p>
                </div>
                <Toggle
                  checked={isFormEnabled}
                  onChange={() => handleDisableToggle()}
                  disabled={!enableEditing}
                  label="Toggle lead intake form enabled"
                />
              </div>

              {/* Disable confirmation dialog */}
              {showDisableConfirm && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-3">
                    Disabling the form will show a &quot;temporarily unavailable&quot; message to visitors.
                    Your configuration will <strong>not</strong> be lost and will be restored when you re-enable.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={confirmDisable}
                      className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                    >
                      Disable Form
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDisableConfirm(false)}
                      className="px-3 py-1.5 text-sm font-medium text-secondary-theme border border-theme rounded-lg hover:bg-subtle transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* When form is enabled, show remaining Lead Capture config in 2-col grid */}
            {isFormEnabled && (
              <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-5 lg:space-y-0 lg:items-start">
                {/* Booking URL */}
                <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-primary-theme">Booking URL</h2>
                    <CardEditButton
                      editing={slugEditing}
                      onEdit={() => { setEditSlug(savedSlug); setSlugEditing(true); }}
                      onSave={handleSlugSave}
                      onCancel={handleSlugCancel}
                      saving={slugSaving}
                    />
                  </div>

                  {bookingUrl && (
                    <div className="flex items-center gap-2 mb-3">
                      <a
                        href={`https://${bookingUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-subtle rounded-lg px-3 py-2.5 text-xs text-plum font-mono truncate hover:underline"
                      >
                        https://{bookingUrl}
                      </a>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-theme hover:bg-subtle transition-colors"
                        aria-label="Copy URL"
                      >
                        {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-secondary-theme" />}
                      </button>
                    </div>
                  )}

                  <div className="mt-2">
                    <label className="text-xs font-medium text-secondary-theme block mb-1">
                      URL Slug
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1 flex items-center border border-theme rounded-lg overflow-hidden">
                        <span className="pl-3 text-xs text-secondary-theme whitespace-nowrap">{BOOKING_DOMAIN}/</span>
                        <input
                          type="text"
                          value={slug}
                          onChange={e => {
                            setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                            setSlugError(null);
                          }}
                          disabled={!slugEditing}
                          className="flex-1 py-2 pr-3 text-sm text-primary-theme bg-transparent focus:outline-none disabled:text-secondary-theme disabled:cursor-not-allowed"
                          placeholder="your-company"
                          maxLength={60}
                        />
                      </div>
                    </div>
                    {slugError && (
                      <p className="text-xs text-red-500 mt-1">{slugError}</p>
                    )}
                    <p className="text-xs text-secondary-theme mt-1">Must be unique. Lowercase letters, numbers, and hyphens only.</p>
                  </div>
                </section>

                {/* Welcome Message */}
                <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-primary-theme">Welcome Message</h2>
                    <CardEditButton
                      editing={welcomeEditing}
                      onEdit={() => { setWelcomeConfig({}); setWelcomeEditing(true); }}
                      onSave={handleWelcomeSave}
                      onCancel={handleWelcomeCancel}
                      saving={welcomeSaving}
                    />
                  </div>
                  <p className="text-xs text-secondary-theme mb-2">
                    Optional text shown at the top of the booking form.
                  </p>
                  <textarea
                    value={effectiveWelcomeMessage}
                    onChange={e => setWelcomeConfig(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                    disabled={!welcomeEditing}
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm text-primary-theme bg-surface border border-theme rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/40 resize-none disabled:bg-subtle disabled:cursor-not-allowed"
                    placeholder="Get a free estimate for your upcoming move!"
                  />
                  <p className="text-xs text-secondary-theme mt-1 text-right">
                    {effectiveWelcomeMessage.length}/500
                  </p>
                </section>

                {/* Customer Actions */}
                <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-primary-theme">Customer Actions</h2>
                    <CardEditButton
                      editing={actionsEditing}
                      onEdit={() => { setActionsConfig({}); setActionsEditing(true); }}
                      onSave={handleActionsSave}
                      onCancel={handleActionsCancel}
                      saving={actionsSaving}
                    />
                  </div>

                  {validationError && (
                    <p className="text-xs text-red-500">{validationError}</p>
                  )}
                  {actionsError && (
                    <p className="text-xs text-red-500">{actionsError}</p>
                  )}

                  {/* Show Price Estimate */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-primary-theme font-medium">Show Price Estimate</p>
                      <p className="text-xs text-secondary-theme">Allow customers to view an estimate based on your configured settings.</p>
                    </div>
                    <Toggle
                      checked={effectiveShowPrice}
                      onChange={v => handleActionsToggle('enableShowPriceEstimate', v)}
                      disabled={!actionsEditing}
                      label="Toggle Show Price Estimate"
                    />
                  </div>

                  {/* Rate Cards callout */}
                  <div className="bg-plum/5 border border-plum/20 rounded-lg p-3 flex items-start gap-2">
                    <Info size={14} className="text-plum shrink-0 mt-0.5" />
                    <p className="text-xs text-secondary-theme">
                      Estimate pricing is based on your org&apos;s rate configuration. You can update pricing in the{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/admin/quoting/rate-cards')}
                        className="text-plum hover:underline font-medium"
                      >
                        Rate Cards
                      </button>{' '}
                      page.
                    </p>
                  </div>

                  {/* Lock In Moving Date */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-primary-theme font-medium">Lock In Moving Date</p>
                      <p className="text-xs text-secondary-theme">Requires a refundable deposit.</p>
                    </div>
                    <Toggle
                      checked={effectiveLockInDate}
                      onChange={v => handleActionsToggle('enableLockInDate', v)}
                      disabled={!actionsEditing}
                      label="Toggle Lock In Moving Date"
                    />
                  </div>

                  {/* Deposit config link — deposit amount is now configured in Customer Payments */}
                  {effectiveLockInDate && (
                    <div className="bg-plum/5 border border-plum/20 rounded-lg p-3 flex items-start gap-2">
                      <Info size={14} className="text-plum shrink-0 mt-0.5" />
                      <p className="text-xs text-secondary-theme">
                        Deposit amount and payment schedule are configured in{' '}
                        <button
                          type="button"
                          onClick={() => navigate('/admin/payments/payment-methods')}
                          className="text-plum hover:underline font-medium"
                        >
                          Customer Payments
                        </button>.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-primary-theme font-medium">Request a Free Estimate</p>
                      <p className="text-xs text-secondary-theme">Customer requests someone reach out.</p>
                    </div>
                    <Toggle
                      checked={effectiveRequestEstimate}
                      onChange={v => handleActionsToggle('enableRequestEstimate', v)}
                      disabled={!actionsEditing}
                      label="Toggle Request a Free Estimate"
                    />
                  </div>
                </section>

                {/* Customer Discovery Sources */}
                <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-primary-theme">Customer Discovery Sources</h2>
                    <CardEditButton
                      editing={sourcesEditing}
                      onEdit={() => { setSourcesConfig({}); setSourcesEditing(true); }}
                      onSave={handleSourcesSave}
                      onCancel={handleSourcesCancel}
                      saving={sourcesSaving}
                    />
                  </div>
                  <p className="text-xs text-secondary-theme mb-3">
                    These options appear in the &quot;How did you hear about us?&quot; dropdown on the booking form.
                  </p>

                  <div className="mb-3">
                    <p className="text-xs font-medium text-secondary-theme uppercase tracking-wider mb-2">Platform Sources</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PLATFORM_SOURCES.map(([key, label]) => (
                        <span
                          key={key}
                          className="inline-flex items-center bg-subtle text-secondary-theme text-xs rounded-full px-3 py-1.5"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-secondary-theme uppercase tracking-wider mb-2">Custom Sources</p>
                    {effectiveCustomSources.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {effectiveCustomSources.map(source => (
                          <span
                            key={source}
                            className="inline-flex items-center gap-1.5 bg-plum/10 text-plum text-xs font-medium rounded-full px-3 py-1.5"
                          >
                            {source}
                            {sourcesEditing && (
                              <button
                                type="button"
                                onClick={() => removeCustomSource(source)}
                                className="hover:text-rose transition-colors"
                                aria-label={`Remove ${source}`}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}

                    {sourcesEditing && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newSource}
                          onChange={e => setNewSource(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addCustomSource();
                            }
                          }}
                          className="flex-1 px-3 py-2 text-sm text-primary-theme bg-surface border border-theme rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/40"
                          placeholder="e.g. Company Newsletter"
                          maxLength={100}
                        />
                        <button
                          type="button"
                          onClick={addCustomSource}
                          disabled={!newSource.trim()}
                          className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-plum text-white hover:bg-plum/90 disabled:opacity-40 transition-colors"
                          aria-label="Add source"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}

                    {!sourcesEditing && effectiveCustomSources.length === 0 && (
                      <p className="text-xs text-secondary-theme italic">No custom sources added.</p>
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            ONLINE & PDF QUOTES TAB
            ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'online_pdf_quotes' && (
          <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-5 lg:space-y-0 lg:items-start">
            {/* ── Online Quotes card ── */}
            <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-primary-theme">Online Quotes</h2>
                <CardEditButton
                  editing={enableEditing}
                  onEdit={() => { setEnableConfig({}); setEnableEditing(true); }}
                  onSave={handleEnableSave}
                  onCancel={handleEnableCancel}
                  saving={enableSaving}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-theme font-medium">Online Quote for Customers</p>
                  <p className="text-xs text-secondary-theme mt-0.5">
                    {isOnlineQuoteEnabled
                      ? 'Customers can view their quotes online through a generated link.'
                      : 'Online quote sharing is disabled.'}
                  </p>
                </div>
                <Toggle
                  checked={isOnlineQuoteEnabled}
                  onChange={v => handleOnlineQuoteToggle(v)}
                  disabled={!enableEditing}
                  label="Toggle online quote for customers"
                />
              </div>

              {/* Payment methods callout */}
              <div className="bg-plum/5 border border-plum/20 rounded-lg p-3 flex items-start gap-2">
                <Info size={14} className="text-plum shrink-0 mt-0.5" />
                <p className="text-xs text-secondary-theme">
                  To configure how customers can pay, go to the{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/admin/payments/payment-methods')}
                    className="text-plum hover:underline font-medium"
                  >
                    Payment Methods
                  </button>{' '}
                  page.
                </p>
              </div>
            </section>

            {/* ── PDF Quotes card ── */}
            {allowedQuotePdfs && (
              <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-primary-theme">PDF Quotes</h2>
                  <CardEditButton
                    editing={pdfEditing}
                    onEdit={handlePdfEdit}
                    onSave={handlePdfSave}
                    onCancel={handlePdfCancel}
                    saving={pdfSaving}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-primary-theme font-medium">Quote PDFs</p>
                    <p className="text-xs text-secondary-theme mt-0.5">
                      {isPdfEnabled
                        ? 'Generate PDFs when creating quote versions.'
                        : 'PDF generation is disabled.'}
                    </p>
                  </div>
                  <Toggle
                    checked={isPdfEnabled}
                    onChange={v => setPdfEnabled(v)}
                    disabled={!pdfEditing}
                    label="Toggle quote PDFs"
                  />
                </div>

                {isPdfEnabled && (
                  <div>
                    <label className="text-xs font-medium text-secondary-theme block mb-1">
                      Quote PDF Footer
                    </label>
                    <textarea
                      value={pdfFooter}
                      onChange={e => setPdfFooter(e.target.value)}
                      disabled={!pdfEditing}
                      rows={4}
                      placeholder="Enter the text you want to appear at the bottom of generated quote PDFs..."
                      className="w-full px-3 py-2.5 text-sm text-primary-theme bg-surface border border-theme rounded-lg
                                 placeholder:text-secondary-theme/50 resize-y
                                 focus:outline-none focus:ring-2 focus:ring-plum/40
                                 disabled:bg-subtle disabled:cursor-not-allowed
                                 transition-colors duration-150"
                    />
                    <p className="text-xs text-secondary-theme mt-1">
                      Text that appears at the bottom of generated quote PDFs.
                    </p>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
